/**
 * AppBackground — decorative fading-sky + grass scene behind the light tabs
 * (Dictionary, Profile): a blue band at the top easing to white, sun + clouds,
 * and the bottom grass band (iOS ProfileBackground). The Home tab does NOT use
 * this — it renders the iOS-exact Home/SkyBackground (dark sky, no grass).
 *
 * Full-bleed (position:fixed) so the sky reaches the top corners and the
 * grass sits on the real viewport bottom; the sun + clouds hug the centered
 * max-600px content column so they sit with the content on wide screens.
 */
import { FC } from "react";
import { cloud1, cloud2, sunFace, sunRays } from "./Profile/profileAssets";

const SUN_SIZE = 86;
const GRASS_VB_WIDTH = 390; // iOS reference device width for the grass-blade math.
const COLUMN_WIDTH = 600; // matches PageContainer / .appPadding.

// Home sky gradient — iOS SkyBackground.tsx:17,26 (darkBlue → blue at 60% →
// white). Exported so HomeLayout's fixed top header strip can paint the SAME
// gradient (sized to the viewport) and blend seamlessly with the Home tab's
// SkyBackground while still occluding scrolled content.
export const SKY_BLUE =
  "linear-gradient(180deg, #003d8e 0%, #0c92f1 60%, #FFFFFF 100%)";
// Fading sky (Dictionary/Profile) — 220px band, blue@0.5 → #E0F4FF@0.1
// (iOS ProfileBackground skyGradient: blue 0.5 at 0% → #E0F4FF 0.1 at 100%).
export const SKY_FADE =
  "linear-gradient(to bottom, rgba(12,146,241,0.5) 0%, rgba(224,244,255,0.1) 100%)";
/** Height of the fading sky band (iOS ProfileBackground sky section: 220px). */
export const SKY_FADE_BAND_HEIGHT = 220;

/** Sun + clouds, anchored to the centered content column. Clipped to the
 * column so decor cuts off at the column edge (the iOS phone-edge cutoff)
 * instead of spilling into the empty side gutters on wide screens. */
const SkyDecor: FC = () => (
  <div
    style={{
      position: "absolute",
      top: 0,
      bottom: 0,
      left: "50%",
      transform: "translateX(-50%)",
      width: "100%",
      maxWidth: COLUMN_WIDTH,
      overflow: "hidden",
    }}
  >
    {/* Sun FIRST so the clouds (rendered after) sit in FRONT of it — iOS layers
        the spinning sun behind the clouds, not on top. */}
    <div
      style={{
        position: "absolute",
        right: 16,
        top: 75,
        width: SUN_SIZE,
        height: SUN_SIZE,
        opacity: 0.85,
      }}
    >
      <img
        src={sunRays}
        alt=""
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "contain",
          animation: "parrot-sun-spin 24s linear infinite",
        }}
      />
      <img
        src={sunFace}
        alt=""
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "contain",
        }}
      />
    </div>
    {/* Cloud PNGs (no edge-fade here — the fade is Home-only; Profile/Dictionary
        keep the original column-edge clip via the parent's overflow:hidden). */}
    <div style={{ position: "absolute", inset: 0 }}>
      <img
        src={cloud1}
        alt=""
        style={{
          position: "absolute",
          left: 5,
          top: 95,
          width: 100,
          height: 70,
          objectFit: "contain",
          opacity: 0.7,
        }}
      />
      <img
        src={cloud2}
        alt=""
        style={{
          position: "absolute",
          left: "50%",
          top: 40,
          width: 120,
          height: 95,
          transform: "translateX(-50%)",
          objectFit: "contain",
          opacity: 0.7,
        }}
      />
      <img
        src={cloud1}
        alt=""
        style={{
          position: "absolute",
          right: 55,
          top: 120,
          width: 60,
          height: 40,
          objectFit: "contain",
          opacity: 0.7,
        }}
      />
    </div>
  </div>
);

/** Bottom-anchored grass band (mirrors iOS grassSection), scaled edge-to-edge. */
const ProfileGrass: FC = () => (
  <div
    aria-hidden
    style={{
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: 120,
      overflow: "hidden",
      pointerEvents: "none",
    }}
  >
    <svg
      width="100%"
      height={120}
      viewBox={`0 0 ${GRASS_VB_WIDTH} 120`}
      preserveAspectRatio="none"
      style={{ position: "absolute", bottom: 0, left: 0 }}
    >
      <defs>
        <linearGradient id="appGrassGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#7CB342" stopOpacity={0.4} />
          <stop offset="100%" stopColor="#558B2F" stopOpacity={0.3} />
        </linearGradient>
      </defs>
      <path
        d={`M0,30 Q${GRASS_VB_WIDTH * 0.25},20 ${GRASS_VB_WIDTH * 0.5},28 Q${GRASS_VB_WIDTH * 0.75},36 ${GRASS_VB_WIDTH},25 L${GRASS_VB_WIDTH},200 L0,200 Z`}
        fill="url(#appGrassGradient)"
      />
      <path d="M20,30 L25,10 L30,30" fill="#8BC34A" opacity={0.6} />
      <path d="M35,32 L38,15 L41,32" fill="#7CB342" opacity={0.5} />
      <path d="M50,28 L55,8 L60,28" fill="#9CCC65" opacity={0.6} />
      <path d="M70,30 L73,18 L76,30" fill="#8BC34A" opacity={0.5} />
      <path
        d={`M${GRASS_VB_WIDTH * 0.3},28 L${GRASS_VB_WIDTH * 0.3 + 4},12 L${GRASS_VB_WIDTH * 0.3 + 8},28`}
        fill="#7CB342"
        opacity={0.5}
      />
      <path
        d={`M${GRASS_VB_WIDTH * 0.4},30 L${GRASS_VB_WIDTH * 0.4 + 5},8 L${GRASS_VB_WIDTH * 0.4 + 10},30`}
        fill="#8BC34A"
        opacity={0.6}
      />
      <path
        d={`M${GRASS_VB_WIDTH * 0.5},26 L${GRASS_VB_WIDTH * 0.5 + 3},14 L${GRASS_VB_WIDTH * 0.5 + 6},26`}
        fill="#9CCC65"
        opacity={0.5}
      />
      <path
        d={`M${GRASS_VB_WIDTH * 0.6},29 L${GRASS_VB_WIDTH * 0.6 + 4},10 L${GRASS_VB_WIDTH * 0.6 + 8},29`}
        fill="#7CB342"
        opacity={0.6}
      />
      <path
        d={`M${GRASS_VB_WIDTH - 80},27 L${GRASS_VB_WIDTH - 76},12 L${GRASS_VB_WIDTH - 72},27`}
        fill="#8BC34A"
        opacity={0.5}
      />
      <path
        d={`M${GRASS_VB_WIDTH - 55},25 L${GRASS_VB_WIDTH - 50},6 L${GRASS_VB_WIDTH - 45},25`}
        fill="#9CCC65"
        opacity={0.6}
      />
      <path
        d={`M${GRASS_VB_WIDTH - 30},28 L${GRASS_VB_WIDTH - 27},15 L${GRASS_VB_WIDTH - 24},28`}
        fill="#7CB342"
        opacity={0.5}
      />
      <circle cx={90} cy={28} r={3} fill="#FFEB3B" opacity={0.7} />
      <circle
        cx={GRASS_VB_WIDTH * 0.45}
        cy={26}
        r={2.5}
        fill="#E91E63"
        opacity={0.5}
      />
      <circle
        cx={GRASS_VB_WIDTH - 100}
        cy={26}
        r={3}
        fill="#FFEB3B"
        opacity={0.6}
      />
    </svg>
  </div>
);

interface AppBackgroundProps {
  // Only the "fade" scene remains — the Home tab renders Home/SkyBackground.
  variant?: "fade";
}

const AppBackground: FC<AppBackgroundProps> = () => (
  <div
    aria-hidden
    style={{
      position: "fixed",
      inset: 0,
      overflow: "hidden",
      pointerEvents: "none",
      zIndex: 0,
    }}
  >
    <style>{`@keyframes parrot-sun-spin { to { transform: rotate(360deg); } }`}</style>

    {/* Top sky band fading to white; the rest of the page stays light. */}
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: SKY_FADE_BAND_HEIGHT,
        overflow: "hidden",
      }}
    >
      <div style={{ position: "absolute", inset: 0, background: SKY_FADE }} />
      <SkyDecor />
    </div>

    <ProfileGrass />
  </div>
);

export default AppBackground;
